import React, { useState, useMemo, useEffect, useCallback } from "react";
import {
    Box,
    Button,
    BottomNavigation,
    BottomNavigationAction,
    Paper,
} from "@mui/material/";
import { TabContext, TabPanel } from "@mui/lab/";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import SearchIcon from "@mui/icons-material/Search";
import ListIcon from "@mui/icons-material/List";
import AuditCompletion from "./AuditCompletion";
import makeStyles from "@mui/styles/makeStyles";
import {
    useToaster,
    useAudit,
    useAuditProgressDetails,
    useGroupAuditProgressDetails,
} from "../common/hooks";

import actions from "../Store/actions";
import styles from "./styles";
import Assets from "./Assets";
import Search from "./Search";
import AuditCompletePrompt from "./AuditCompletePrompt";
import { logToSentry } from "../config/Sentry";

const AuditAssets = () => {
    const { t } = useTranslation();
    const toast = useToaster();
    const [bottomTab, setBottomTab] = useState(t("search"));
    const [assetTab, setAssetTab] = useState("unchecked");
    const classes = makeStyles(styles)();
    const { auditId } = useParams();
    const dispatch = useDispatch();
    const { auditAssignments } = useSelector((state) => state);
    const audit = useAudit(auditId);
    const { isComplete } = useAuditProgressDetails("store", auditId);
    const { groupedComplete } = useGroupAuditProgressDetails("store", auditId);

    /** Show complete audit popup */
    const showCompleteAuditPopup = useMemo(() => {
        const isGroupAudit = audit?.auditReference?.toLowerCase() !== "none";
        if (isGroupAudit) return groupedComplete;
        else return isComplete;
    }, [audit, isComplete, groupedComplete]);

    const onRefresh = useCallback(() => {
        const selectData = async () => {
            try {
                if (auditAssignments && audit) {
                    const assignmentsIds = [];
                    const locIds = [];
                    let auditReference;

                    auditAssignments.data.forEach((item) => {
                        if (item.auditLocation.audit.id === audit.id) {
                            /** Check for audit reference */
                            !isNaN(
                                parseInt(
                                    item.auditLocation.audit.auditReference
                                )
                            )
                                ? (auditReference = parseInt(
                                      item.auditLocation.audit.auditReference
                                  ))
                                : null;
                            /*** Check audit reference end  */

                            locIds.push(item.auditLocation.id);
                            assignmentsIds.push(item.id);
                        }
                    });

                    if (auditReference) {
                        await Promise.all([
                            dispatch(
                                actions.auditAssets.create.selectGroupRequest(
                                    auditReference
                                )
                            ),
                            dispatch(
                                actions.altAssets.create.selectRequest(auditId)
                            ),
                        ]);
                    } else
                        await Promise.all([
                            dispatch(
                                actions.auditAssets.create.selectRequest(locIds)
                            ),
                            dispatch(
                                actions.altAssets.create.selectRequest(auditId)
                            ),
                        ]);
                }
            } catch (error) {
                console.log(error);
                toast.error(t("errors.auditAssetsGet"));
                logToSentry(`on select data:`, {
                    error,
                    errStr: error.toString(),
                });
            }
        };
        selectData();
    }, [auditAssignments, audit]);

    useEffect(() => {
        onRefresh();
    }, [auditAssignments, audit]);

    useEffect(() => {
        try {
            dispatch(actions.assetSync.create.start());
            dispatch(actions.geoLocation.create.watchStart());
        } catch (error) {
            console.log(error);
            toast.error(t("errors.assetSyncGeoLocations"));
            logToSentry(`UE: assetSyncGeoLocations:`, {
                error,
                errStr: error.toString(),
            });
        }

        return () => {
            dispatch(actions.assetSync.create.stop());
            dispatch(actions.geoLocation.create.watchStop());
        };
    }, []);

    const handleChange = (event, newValue) => {
        setBottomTab(newValue);
    };

    const onAssetChange = (name) => () => {
        const value = name.toLowerCase();
        setAssetTab(value);
    };

    return (
        <>
            {showCompleteAuditPopup ? (
                <AuditCompletePrompt message={isComplete} />
            ) : null}

            <AuditCompletion />
            <Box className={classes.tabContainer}>
                {bottomTab === t("assets") ? (
                    <Box className={classes.assetListButtonsContainer}>
                        <Button
                            className={`${classes.assetListButtons} ${
                                assetTab.toLowerCase() === "checked"
                                    ? classes.activeListButton
                                    : ""
                            }`}
                            onClick={onAssetChange("checked")}
                        >
                            {t("checked")}
                        </Button>
                        <Button
                            className={`${classes.assetListButtons} ${
                                assetTab.toLowerCase() === "unchecked"
                                    ? classes.activeListButton
                                    : ""
                            }`}
                            onClick={onAssetChange("unchecked")}
                        >
                            {t("unchecked")}
                        </Button>
                    </Box>
                ) : null}
                <TabContext value={bottomTab}>
                    <TabPanel
                        value={t("assets")}
                        className={classes.tabPanelContainer}
                    >
                        <Assets assetListType={assetTab} />
                    </TabPanel>
                    <TabPanel value={t("search")}>
                        <Search />
                    </TabPanel>
                </TabContext>
            </Box>
            <Paper className={classes.bottomNavPaper} elevation={3}>
                <BottomNavigation
                    showLabels
                    value={bottomTab}
                    onChange={handleChange}
                    aria-label="Bottom Navigation"
                >
                    <BottomNavigationAction
                        label={t("assets")}
                        icon={<ListIcon />}
                        value={t("assets")}
                        className={`${classes.buttonTab} ${
                            bottomTab === t("assets")
                                ? classes.activeBottomTab
                                : null
                        }`}
                    />
                    <BottomNavigationAction
                        label={t("search")}
                        icon={<SearchIcon />}
                        value={t("search")}
                        className={`${classes.buttonTab} ${
                            bottomTab === t("search")
                                ? classes.activeBottomTab
                                : null
                        }`}
                    />
                </BottomNavigation>
            </Paper>
        </>
    );
};

export default AuditAssets;

/** TODO
 * 1. Check if there is a better way of checking auditReference
 * 2. Cleanup if there is a better way
 */
